/**
 * Ordem e situação de prazo das tarefas na aba "Tarefas" do projeto.
 *
 * A consulta ao banco não tem `order`, então sem isso a lista sai na ordem física da tabela
 * — que muda a cada UPDATE e embaralha "09." antes de "07.".
 */
export interface TarefaOrdenavel {
  id: number | string;
  titulo?: string | null;
  vencimento?: string | null;
  solicitacao_status?: string | null;
  status?: string | null;
}

export type SituacaoPrazo = 'atrasada' | 'vence_hoje' | 'proxima' | 'no_prazo' | 'sem_prazo' | 'encerrada';

/** Até quantos dias à frente o vencimento conta como "próximo". */
export const DIAS_PROXIMO = 3;

const ENCERRADOS = new Set(['concluido', 'rejeitado', 'cancelado']);

const situacao = (t: TarefaOrdenavel): string => t.solicitacao_status || t.status || '';

/** Número do prefixo "07." / "7 -" / "07)" do título; null quando o título não é numerado. */
export const numeroDoTitulo = (titulo?: string | null): number | null => {
  const m = (titulo || '').trim().match(/^(\d+)\s*[.)\-–:]/);
  return m ? Number(m[1]) : null;
};

const diaDoVencimento = (v?: string | null): number | null =>
  v ? new Date(`${v.slice(0, 10)}T00:00:00`).getTime() : null;

/**
 * Numeradas primeiro, pela numeração do título (é a ordem que quem montou o projeto escolheu).
 * As sem número vêm depois, por vencimento (sem vencimento no fim) e, no empate, pela criação (id).
 */
export const ordenarTarefas = <T extends TarefaOrdenavel>(tarefas: T[]): T[] =>
  [...tarefas].sort((a, b) => {
    const na = numeroDoTitulo(a.titulo);
    const nb = numeroDoTitulo(b.titulo);
    if (na != null && nb != null && na !== nb) return na - nb;
    if (na != null && nb == null) return -1;
    if (na == null && nb != null) return 1;
    const va = diaDoVencimento(a.vencimento) ?? Infinity;
    const vb = diaDoVencimento(b.vencimento) ?? Infinity;
    if (va !== vb) return va - vb;
    return Number(a.id) - Number(b.id);
  });

/** Dias até o vencimento (negativo = atrasada). null sem vencimento. */
export const diasParaVencer = (t: TarefaOrdenavel, hoje: Date = new Date()): number | null => {
  const dia = diaDoVencimento(t.vencimento);
  if (dia == null) return null;
  const inicioDoDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
  return Math.round((dia - inicioDoDia) / 86_400_000);
};

/** Concluída/rejeitada/cancelada não atrasa: prazo só importa para o que ainda está aberto. */
export const situacaoPrazo = (t: TarefaOrdenavel, hoje: Date = new Date()): SituacaoPrazo => {
  if (ENCERRADOS.has(situacao(t))) return 'encerrada';
  const dias = diasParaVencer(t, hoje);
  if (dias == null) return 'sem_prazo';
  if (dias < 0) return 'atrasada';
  if (dias === 0) return 'vence_hoje';
  if (dias <= DIAS_PROXIMO) return 'proxima';
  return 'no_prazo';
};

/** Texto curto do selo de prazo; null quando não há o que destacar. */
export const rotuloPrazo = (t: TarefaOrdenavel, hoje: Date = new Date()): string | null => {
  const dias = diasParaVencer(t, hoje);
  switch (situacaoPrazo(t, hoje)) {
    case 'atrasada': return dias === -1 ? 'Atrasada 1 dia' : `Atrasada ${-(dias as number)} dias`;
    case 'vence_hoje': return 'Vence hoje';
    case 'proxima': return dias === 1 ? 'Vence amanhã' : `Vence em ${dias} dias`;
    default: return null;
  }
};
